import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { Building, DollarSign, Wrench, Briefcase, TrendingUp, Users, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const BiddingIntelligence = () => {
  const [biddingData, setBiddingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('estimators');
  const [sortConfig, setSortConfig] = useState({ key: 'winRate', direction: 'desc' });

  useEffect(() => {
    const loadRealData = async () => {
      try {
        const response = await fetch('/api/dashboard-data/raw-project-analysis', { credentials: 'same-origin' });
        if (response.ok) {
          const data = await response.json();
          setBiddingData(data);
        } else {
          console.error('Failed to load bidding intelligence data');
        }
      } catch (error) {
        console.error('Error loading bidding intelligence data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRealData();
  }, []);

  useEffect(() => {
    document.title = 'Bidding Intelligence (Sylvan Canada)';
  }, []);

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const formatNumber = (value) => {
    return value.toLocaleString();
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );



  // Function to sort estimators
  const sortEstimators = (estimators, sortKey, direction) => {
    return [...estimators].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortKey) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'totalBids':
          aValue = a.totalBids;
          bValue = b.totalBids;
          break;
        case 'wonBids':
          aValue = a.wonBids;
          bValue = b.wonBids;
          break;
        case 'lostBids':
          aValue = a.lostBids;
          bValue = b.lostBids;
          break;
        case 'winRate':
          aValue = a.winRate;
          bValue = b.winRate;
          break;
        case 'totalValue':
          aValue = a.totalValue;
          bValue = b.totalValue;
          break;
        case 'avgBidValue':
          aValue = a.avgBidValue;
          bValue = b.avgBidValue;
          break;

        default:
          aValue = a[sortKey];
          bValue = b[sortKey];
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Function to handle sorting
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Function to get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading bidding intelligence data...</div>
      </div>
    );
  }

  if (!biddingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Failed to load bidding intelligence data</div>
      </div>
    );
  }

  const { overview, estimators, yearlyTrends } = biddingData;

  // Filter estimators with 10+ bids and sort based on current sort configuration
  const filteredEstimators = estimators.filter(estimator => estimator.totalBids >= 10);
  const sortedEstimators = sortEstimators(filteredEstimators, sortConfig.key, sortConfig.direction);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Bidding Intelligence (Sylvan Canada)</h1>
        <p className="text-gray-600">Comprehensive analysis of bidding performance and trends</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Projects"
          value={formatNumber(overview.totalProjects)}
          subtitle="All tracked bids"
          icon={Building}
          color="blue"
        />
        <StatCard
          title="Win Rate"
          value={`${(overview.winRate * 100).toFixed(1)}%`}
          subtitle={`${overview.wonBids} won / ${overview.lostBids} lost`}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Total Value"
          value={formatCurrency(overview.totalValue)}
          subtitle={`Avg: ${formatCurrency(overview.avgProjectValue)}`}
          icon={DollarSign}
          color="yellow"
        />
        <StatCard
          title="Active Estimators"
          value={estimators.length}
          subtitle="Team members"
          icon={Users}
          color="purple"
        />
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'estimators', label: 'Estimators' },
            { id: 'trends', label: 'Yearly Trends' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>



      {activeTab === 'estimators' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Bidding Stats</h3>
            <p className="text-sm text-gray-600 mt-1">
              Click column headers to sort. Rankings are based on win rate. Showing estimators with 10+ bids.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Estimator</span>
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('totalBids')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Bids</span>
                      {getSortIcon('totalBids')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('wonBids')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Won</span>
                      {getSortIcon('wonBids')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('lostBids')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lost</span>
                      {getSortIcon('lostBids')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('winRate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Win Rate</span>
                      {getSortIcon('winRate')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('totalValue')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Value</span>
                      {getSortIcon('totalValue')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('avgBidValue')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Avg Bid Value</span>
                      {getSortIcon('avgBidValue')}
                    </div>
                  </th>

                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedEstimators.map((estimator, index) => (
                  <tr key={estimator.name}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {estimator.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {estimator.totalBids}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {estimator.wonBids}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {estimator.lostBids}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(estimator.winRate * 100).toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(estimator.totalValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(estimator.avgBidValue)}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'trends' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Yearly Trends (2014-2024)</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={yearlyTrends.filter(trend => trend.year !== '2014')}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'winRate' ? `${value.toFixed(1)}%` : value, 
                  name === 'won' ? 'Won' : name === 'lost' ? 'Lost' : name === 'winRate' ? 'Win Rate' : 'Total'
                ]}
              />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="won" 
                stroke="#10B981" 
                strokeWidth={2}
                name="Won"
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="lost" 
                stroke="#EF4444" 
                strokeWidth={2}
                name="Lost"
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="winRate" 
                stroke="#8B5CF6" 
                strokeWidth={3}
                name="Win Rate"
                dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default BiddingIntelligence; 